import {
  ActionCreatorWithoutPayload,
  ActionCreatorWithPayload,
  createListenerMiddleware,
  ListenerEffectAPI,
} from '@reduxjs/toolkit';

import { AppDispatch } from '..';
import { AppState } from '../reducers/root';

export const createAppListener = () => createListenerMiddleware<AppState>();

type AsyncActions<TPayload, TSuccess> = {
  request: ActionCreatorWithPayload<TPayload> | ActionCreatorWithoutPayload;
  success: ActionCreatorWithPayload<TSuccess>;
  failure: ActionCreatorWithPayload<Error>;
};

export const apiCall = <TPayload, TSuccess>(
  actions: AsyncActions<TPayload, TSuccess>,
  handler: (
    payload: TPayload,
    state: AppState,
    dispatch: AppDispatch,
    getState: () => AppState
  ) => Promise<TSuccess>
) => ({
  actionCreator: actions.request,
  effect: async (action: any, api: ListenerEffectAPI<AppState, any>) => {
    try {
      const result = await handler(
        action.payload,
        api.getState(),
        api.dispatch,
        api.getState
      );
      api.dispatch(actions.success(result));
    } catch (error) {
      api.dispatch(actions.failure(error as Error));
    }
  },
});

export const postDispatch = <T>(
  actionCreator: ActionCreatorWithPayload<T> | ActionCreatorWithoutPayload,
  handler: (
    payload: T,
    state: AppState,
    dispatch: AppDispatch,
    prevState: AppState,
    getState: () => AppState
  ) => void
) => ({
  actionCreator,
  effect: (action: any, api: ListenerEffectAPI<AppState, any>) => {
    handler(
      action.payload,
      api.getState(),
      api.dispatch,
      api.getOriginalState(),
      api.getState
    );
  },
});

export const postDispatchAny = <T = any>(
  actionCreators: Array<
    ActionCreatorWithPayload<any> | ActionCreatorWithoutPayload
  >,
  handler: (
    payload: T,
    state: AppState,
    dispatch: AppDispatch,
    prevState: AppState,
    actionType: string
  ) => void
) => ({
  predicate: (action: any) => actionCreators.some(ac => ac.match(action)),
  effect: (action: any, api: ListenerEffectAPI<AppState, any>) => {
    handler(
      action.payload,
      api.getState(),
      api.dispatch,
      api.getOriginalState(),
      action.type
    );
  },
});

export const onceInit = (
  init: (state: AppState, dispatch: AppDispatch) => void
) => ({
  predicate: () => true,
  effect: (_action: any, api: ListenerEffectAPI<AppState, any>) => {
    init(api.getState(), api.dispatch);
    api.unsubscribe();
  },
});
